import { Edit2, Trash2, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PhysicalAssessment } from '@/data/mockAssessments';
import { useState } from 'react';
import { DeleteConfirmModal } from './DeleteConfirmModal';

interface AssessmentCardProps {
  assessment: PhysicalAssessment;
}

export function AssessmentCard({ assessment }: AssessmentCardProps) {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const typeConfig = {
    normal: { 
      color: 'bg-green-100 text-green-700 border-green-200', 
      icon: '✓', 
      label: 'Normal' 
    },
    abnormal: { 
      color: 'bg-orange-100 text-orange-700 border-orange-200', 
      icon: '⚠', 
      label: 'Abnormal' 
    },
    critical: { 
      color: 'bg-red-100 text-red-700 border-red-200', 
      icon: '⚠', 
      label: 'Critical' 
    },
  };

  const systemColors: Record<string, string> = {
    general_constitutional: 'bg-gray-50 border-gray-200',
    general: 'bg-gray-50 border-gray-200',
    heent: 'bg-yellow-50 border-yellow-200',
    ent: 'bg-yellow-50 border-yellow-200',
    neck: 'bg-amber-50 border-amber-200',
    cardiovascular: 'bg-red-50 border-red-200',
    respiratory: 'bg-blue-50 border-blue-200',
    abdominal_gi: 'bg-orange-50 border-orange-200',
    gastrointestinal: 'bg-orange-50 border-orange-200',
    musculoskeletal: 'bg-indigo-50 border-indigo-200',
    neurological: 'bg-purple-50 border-purple-200',
    skin: 'bg-pink-50 border-pink-200',
    dermatological: 'bg-pink-50 border-pink-200',
    psych_mental: 'bg-teal-50 border-teal-200',
    genitourinary: 'bg-cyan-50 border-cyan-200',
  };

  const handleDelete = () => {
    setShowDeleteModal(false);
  };

  return (
    <>
      <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all group">
        {/* Header */}
        <div className={`p-4 border-b-2 ${systemColors[assessment.system] || 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-1">{assessment.name}</h3>
              <span className="text-xs text-gray-600 capitalize">{assessment.system} System</span>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => navigate(`/admin/assessments/${assessment.id}/edit`)}
                className="p-2 hover:bg-white/80 rounded-lg transition-colors"
                aria-label="Edit assessment"
              >
                <Edit2 size={16} className="text-gray-600" />
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="p-2 hover:bg-white/80 rounded-lg transition-colors"
                aria-label="Delete assessment"
              >
                <Trash2 size={16} className="text-red-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-4">
          {/* Type Badge */}
          <div className="mb-4">
            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-medium border ${typeConfig[assessment.type].color}`}>
              <span>{typeConfig[assessment.type].icon}</span>
              <span>{typeConfig[assessment.type].label} Findings</span>
            </span>
          </div>

          {/* Vital Signs (if present) */}
          {assessment.vitalSigns && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-xs font-semibold text-gray-700 mb-2">Vital Signs:</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {assessment.vitalSigns.bp && (
                  <div>
                    <span className="text-gray-600">BP:</span>{' '}
                    <span className="font-semibold text-gray-900">{assessment.vitalSigns.bp}</span>
                  </div>
                )}
                {assessment.vitalSigns.hr && (
                  <div>
                    <span className="text-gray-600">HR:</span>{' '}
                    <span className="font-semibold text-gray-900">{assessment.vitalSigns.hr}</span>
                  </div>
                )}
                {assessment.vitalSigns.rr && (
                  <div>
                    <span className="text-gray-600">RR:</span>{' '}
                    <span className="font-semibold text-gray-900">{assessment.vitalSigns.rr}</span>
                  </div>
                )}
                {assessment.vitalSigns.temp && (
                  <div>
                    <span className="text-gray-600">Temp:</span>{' '}
                    <span className="font-semibold text-gray-900">{assessment.vitalSigns.temp}</span>
                  </div>
                )}
                {assessment.vitalSigns.o2sat && (
                  <div>
                    <span className="text-gray-600">O₂ Sat:</span>{' '}
                    <span className="font-semibold text-gray-900">{assessment.vitalSigns.o2sat}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Physical Findings */}
          <div className="mb-4">
            <div className="text-sm font-semibold text-gray-700 mb-2">Physical Findings:</div>
            <div className="space-y-1">
              {assessment.findings.slice(0, 4).map((finding, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-[#024223] mt-1">•</span>
                  <span>{finding}</span>
                </div>
              ))}
              {assessment.findings.length > 4 && (
                <div className="text-xs text-gray-500 ml-4">
                  +{assessment.findings.length - 4} more findings
                </div>
              )}
            </div>
          </div>

          {/* Usage Stats */}
          <div className="flex items-center justify-between text-sm border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2 text-gray-600">
              <Users size={16} />
              <span>Used by <strong>{assessment.usedBy}</strong> agents</span>
            </div>
            <span className="text-gray-500 text-xs">{assessment.lastUpdated}</span>
          </div>

          {/* View Details Button */}
          <button
            onClick={() => navigate(`/admin/assessments/${assessment.id}/edit`)}
            className="w-full mt-4 py-2 border border-[#024223] text-[#024223] rounded-lg font-medium hover:bg-[#024223] hover:text-white transition-colors"
          >
            View Details
          </button>
        </div>
      </div>

      <DeleteConfirmModal
        agentName={assessment.name}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        open={showDeleteModal}
      />
    </>
  );
}
